/**
 * ContactDetailPageSkeleton
 *
 * Skeleton placeholder shown while the contact detail page's data is
 * loading. Mirrors the high-level shape of `ContactDetailPageCore` so
 * the layout doesn't shift once real data lands:
 *   - DetailHeader strip with stage button + campaign button skeletons
 *   - Tabs skeleton row
 *   - Body filled with the existing animated `LoadingSection` placeholder
 *
 * Pure / display-only — takes no callbacks, runs no effects.
 */
import { cn } from '../cn';
import { ContactInteractSectionPure } from './ContactInteractSectionPure';
import { LoadingSection } from './ContactInteractSectionPure';
import { DetailHeaderContactLoading } from './DetailHeaderContactPure';
import DetailHeader from './DetailHeader';

export type ContactDetailPageSkeletonProps = {
  className?: string;
};

export function ContactDetailPageSkeleton({ className }: ContactDetailPageSkeletonProps) {
  return (
    <div
      data-testid="contact-detail-skeleton"
      className={cn('flex h-full flex-col', className)}>
      <DetailHeader
        className="bg-slate-50 dark:bg-slate-900"
        buttons={<SkeletonStageButton />}
        footer={
          <div className="flex flex-col px-4 py-2">
            <div className="flex items-center justify-end gap-2 pb-5">
              <SkeletonCampaignButton />
            </div>
            <SkeletonTabs />
          </div>
        }>
        <DetailHeaderContactLoading />
      </DetailHeader>
      <div className="relative flex h-0 w-full grow flex-col overflow-hidden">
        <div className="h-full overflow-y-auto bg-stone-50 dark:bg-stone-800">
          <LoadingSection />
        </div>
      </div>
    </div>
  );
}

// Re-export so callers can grab the skeleton bits standalone if needed.
export { ContactInteractSectionPure };

function SkeletonCampaignButton() {
  return <div className="h-8 w-40 animate-pulse rounded-full bg-astronaut-200 dark:bg-astronaut-700" />;
}

function SkeletonStageButton() {
  return <div className="h-6 w-20 animate-pulse rounded-full bg-astronaut-200 dark:bg-astronaut-700" />;
}

function SkeletonTabs() {
  return (
    <div className="flex gap-2 border-t border-astronaut-100 pt-2 dark:border-astronaut-800">
      <div className="h-8 w-20 animate-pulse rounded-sm bg-astronaut-200 dark:bg-astronaut-700" />
      <div className="h-8 w-20 animate-pulse rounded-sm bg-astronaut-200 dark:bg-astronaut-700" />
    </div>
  );
}
